import { useEffect, useRef } from "react";
import { Editor, TLShapeId } from "@tldraw/tldraw";
import { enterMaskEditMode, exitMaskEditMode } from "../utils/booleanOperations";

/**
 * Hook to handle double-click on mask shapes to enter/exit edit mode
 * Also handles automatic exit when selection changes
 */
export function useMaskShapeEditMode(editor: Editor | null) {
  const lastClickRef = useRef<{ shapeId: TLShapeId | null; time: number }>({
    shapeId: null,
    time: 0,
  });
  const currentEditModeRef = useRef<{ maskShapeId: TLShapeId; groupId: TLShapeId } | null>(null);
  const lastSelectionRef = useRef<string>("");
  const isExitingRef = useRef<boolean>(false); // Prevent race conditions

  useEffect(() => {
    if (!editor) return;

    // Auto-detect if a mask group is in edit mode (e.g., after Ctrl+Z)
    const checkForActiveEditMode = () => {
      // Skip if we already know we're in edit mode
      if (currentEditModeRef.current) return;

      // Look for any mask group with inEditMode: true
      const allShapes = editor.getCurrentPageShapes();
      const activeGroup = allShapes.find(
        (s) => s.type === "group" && s.meta?.isMaskGroup && s.meta?.inEditMode === true
      );

      if (activeGroup) {
        // Get the maskShapeId (might not exist if we're in a restored state)
        const maskShapeId = (activeGroup.meta?.maskShapeId as TLShapeId) || (activeGroup.id as TLShapeId);

        // Update our ref to track this edit mode
        currentEditModeRef.current = {
          maskShapeId,
          groupId: activeGroup.id as TLShapeId,
        };

        // Update lastSelection to current visible shapes
        const visibleChildren = editor
          .getSortedChildIdsForParent(activeGroup.id as TLShapeId)
          .map((id) => editor.getShape(id))
          .filter((s) => s && s.meta?.isInMaskGroup);

        lastSelectionRef.current = visibleChildren
          .map((s) => s!.id)
          .sort()
          .join(",");
      }
    };

    // Check immediately on mount
    checkForActiveEditMode();

    // Use store listener instead of polling for better performance
    const unsubscribeStore = editor.store.listen(
      (entry) => {
        const { changes } = entry;

        // Check if any group's inEditMode flag changed
        if (changes.updated) {
          for (const [_id, [_fromRecord, toRecord]] of Object.entries(changes.updated)) {
            const shape = toRecord as any;
            if (shape.type === "group" && shape.meta?.isMaskGroup && shape.meta?.inEditMode === true) {
              checkForActiveEditMode();
              break;
            }
          }
        }
      },
      { source: "user", scope: "document" }
    );

    // Handle selection changes to detect when user deselects the mask group
    const handleSelectionChange = () => {
      // First, auto-detect in case we missed it
      if (!currentEditModeRef.current) {
        checkForActiveEditMode();
      }

      if (!currentEditModeRef.current || isExitingRef.current) return;

      const selectedShapes = editor.getSelectedShapes();

      // Filter out preview shapes (they should be ignored for selection logic)
      const nonPreviewShapes = selectedShapes.filter((s) => !s.meta?.isPreviewShape);
      const selectionKey = nonPreviewShapes
        .map((s) => s.id)
        .sort()
        .join(",");

      // Avoid processing the same selection multiple times
      if (selectionKey === lastSelectionRef.current) return;
      lastSelectionRef.current = selectionKey;

      // If nothing is selected (except preview), exit edit mode
      if (nonPreviewShapes.length === 0) {
        isExitingRef.current = true;
        exitMaskEditMode(currentEditModeRef.current.maskShapeId, editor);
        currentEditModeRef.current = null;
        isExitingRef.current = false;
        return;
      }

      // Check if any selected shape is outside the mask group (ignore preview shapes)
      const allInMaskGroup = nonPreviewShapes.every((shape) => {
        // Allow selecting the mask group itself
        if (shape.id === currentEditModeRef.current?.groupId) {
          return true;
        }
        // Allow selecting shapes inside the mask group
        const isInGroup = shape.meta?.isInMaskGroup && shape.parentId === currentEditModeRef.current?.groupId;
        return isInGroup;
      });

      if (!allInMaskGroup) {
        isExitingRef.current = true;
        exitMaskEditMode(currentEditModeRef.current.maskShapeId, editor);
        currentEditModeRef.current = null;
        lastSelectionRef.current = "";
        isExitingRef.current = false;
      }
    };

    // Handle double-click to enter/exit edit mode
    const handlePointerDown = (event: Event) => {
      const selectedShapes = editor.getSelectedShapes();
      if (selectedShapes.length !== 1) return;

      const shape = selectedShapes[0];
      const now = Date.now();
      const timeSinceLastClick = now - lastClickRef.current.time;
      const isSameShape = lastClickRef.current.shapeId === shape.id;
      const isDoubleClick = isSameShape && timeSinceLastClick < 500;

      if (isDoubleClick) {
        // Check if it's a mask shape OR a composite part
        const isMaskShape = shape.meta?.isMaskShape;
        const isCompositePart = shape.meta?.isCompositePart;

        if (isMaskShape || isCompositePart) {
          // For composite parts, we need to find the parent composite group
          let maskShapeId: TLShapeId;
          let maskGroupId: TLShapeId;

          if (isCompositePart && !isMaskShape) {
            // This is a part of a composite group (2+ parts) - find the composite group
            const parentId = shape.parentId;

            if (!parentId || parentId === "page:page") {
              console.warn("  ⚠️ Composite part has no valid parent group:", { parentId });
              return;
            }

            const parentGroup = editor.getShape(parentId);
            if (!parentGroup || !parentGroup.meta?.isCompositeResult) {
              console.warn("  ⚠️ Parent is not a composite result group");
              return;
            }

            // The composite group is the "mask shape"
            maskShapeId = parentId as TLShapeId;
            maskGroupId = parentGroup.meta.maskGroupId as TLShapeId;
          } else {
            // Regular mask shape (single part OR a composite group itself)
            maskShapeId = shape.id;
            maskGroupId = shape.meta.maskGroupId as TLShapeId;
          }

          if (!maskGroupId) {
            console.warn("  ⚠️ No mask group ID found");
            return;
          }

          // Check if already in edit mode
          const maskGroup = editor.getShape(maskGroupId);
          const inEditMode = maskGroup?.meta?.inEditMode === true;

          if (inEditMode) {
            isExitingRef.current = true;
            exitMaskEditMode(maskShapeId, editor);
            currentEditModeRef.current = null;
            lastSelectionRef.current = "";
            isExitingRef.current = false;
          } else {
            enterMaskEditMode(maskShapeId, editor);
            currentEditModeRef.current = { maskShapeId, groupId: maskGroupId };
            // Update last selection to avoid immediate exit
            const selectedAfterEnter = editor.getSelectedShapes();
            lastSelectionRef.current = selectedAfterEnter
              .map((s) => s.id)
              .sort()
              .join(",");
          }
        }

        lastClickRef.current = { shapeId: null, time: 0 };
      } else {
        lastClickRef.current = { shapeId: shape.id, time: now };
      }
    };

    // Add a capture-phase dblclick handler to prevent crop mode for mask shapes
    // This runs BEFORE the CropComponent's dblclick handler, allowing us to block it
    const handleDoubleClickCapture = (event: MouseEvent) => {
      const selectedShapes = editor.getSelectedShapes();
      if (selectedShapes.length !== 1) {
        return;
      }

      const shape = selectedShapes[0];

      const isMaskShape = shape.meta?.isMaskShape;
      const isCompositePart = shape.meta?.isCompositePart;

      if ((isMaskShape || isCompositePart) && shape.type === "customImage") {
        // Prevent the CropComponent from handling this double-click
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    // Hybrid approach: Listen to shape changes + poll selection changes
    // Store listener detects shape modifications instantly
    const unsubscribeSelection = editor.store.listen(
      (entry) => {
        const { changes } = entry;
        if (changes.updated || changes.added || changes.removed) {
          if (currentEditModeRef.current) {
            handleSelectionChange();
          }
        }
      },
      { source: "user", scope: "document" }
    );

    // Selection state is separate from shape state, so we need polling
    // 100ms is a good balance between responsiveness and performance
    const selectionCheckInterval = setInterval(() => {
      if (currentEditModeRef.current) {
        handleSelectionChange();
      }
    }, 100);

    // Add the dblclick listener with capture phase for priority
    window.addEventListener("dblclick", handleDoubleClickCapture, { capture: true });

    const canvas = document.querySelector(".tl-canvas");
    if (canvas) {
      canvas.addEventListener("pointerdown", handlePointerDown);

      return () => {
        canvas.removeEventListener("pointerdown", handlePointerDown);
        window.removeEventListener("dblclick", handleDoubleClickCapture, { capture: true });
        clearInterval(selectionCheckInterval);
        unsubscribeSelection();
        unsubscribeStore();
      };
    }

    return () => {
      clearInterval(selectionCheckInterval);
      unsubscribeSelection();
      unsubscribeStore();
      window.removeEventListener("dblclick", handleDoubleClickCapture, { capture: true });
    };
  }, [editor]);
}
